import TransactionModal from "./transactionModal.js";
import { $_GET, trans } from "../globalFunctions.js";

export default class TransferModal extends TransactionModal {
  constructor(companyId) {
    super("transfer-modal", companyId);

    this.$date = this.$form.querySelector('[name="date"]');
    this.$availableShares = this.$form.querySelector("[data-available-shares]");
    this.$amount = this.$form.querySelector('[name="amount"]');
    this.$stakeholder = this.$form.querySelector('[name="stakeholder"]');
    this.$shareClass = this.$form.querySelector('[name="share-class"]');

    this.formFields = [
      {
        name: "id",
        varType: "int",
      },
      {
        name: "amount",
        varType: "int",
        required: true,
      },
      {
        name: "beneficiary",
        varType: "int",
      },
      {
        name: "stakeholder",
        varType: "int",
        required: true,
        trigger: "change",
      },
      {
        name: "beneficiary_receiver",
        varType: "int",
      },
      {
        name: "stakeholder_receiver",
        varType: "int",
        required: true,
        trigger: "change",
      },
      {
        name: "date",
        varType: "string",
        required: true,
      },
      {
        name: "price",
        varType: "float",
      },
      {
        name: "share_class",
        varType: "int",
        required: true,
      },
      {
        name: "numeration",
        varType: "string",
        mayNotExist: true,
      },
      {
        name: "documents",
        varType: "simple-array",
        type: "int",
      },
      {
        name: "notes",
      },
    ];

    this.setupForm();

    Promise.all([
      this.documentsPromise,
      this.setupAllStakeholders(),
      this.setupShareClasses(),
    ]).then(() => {
      this.whenReady();
      this.updateAvailableSharesCount();
    });
  }

  setupForm() {
    this.$date.valueAsDate = new Date();

    this.$stakeholder.addEventListener("change", () => {
      this.updateAvailableSharesCount();
    });
    this.$date.addEventListener("change", () => {
      this.updateAvailableSharesCount();
    });
    this.$shareClass.addEventListener("change", () => {
      this.updateAvailableSharesCount();
    });

    this.$amount.addEventListener("input", () => {
      if (this.getInt("amount") > this.$amount.max) {
        this.setInt("amount", this.$amount.max);
      }
    });

    this.$form
      .querySelector('[data-action="autocomplete-numeration"]')
      ?.addEventListener("click", () => {
        this.autocompleteNumeration();
      });
  }

  autocompleteNumeration() {
    const amount = this.getInt("amount");

    if (amount < 1) {
      this.showErrorOnField("", this.$form.querySelector('[name="amount"]'));
      return;
    }

    this.getAvailableNumerationRange(amount).then((numeration) => {
      this.$form.querySelector('[name="numeration"]').value = numeration;
    });
  }

  getAvailableNumerationRange(amount) {
    return new Promise((resolve, reject) => {
      peque
        .api("shares/available_numeration_range", {
          amount: amount,
          company: window.currentCompany.id,
          stakeholder: this.getInt("stakeholder"),
        })
        .then((response) => {
          resolve(response.result);
        });
    });
  }

  updateAvailableSharesCount() {
    const setCount = (count) => {
      this.$availableShares.querySelector("i").innerText =
        peque.formatNumber(count);
      this.$amount.max = count;
    };

    // seems to have selected "Add Stakeholder" option
    if (isNaN(this.$stakeholder.value)) {
      setCount(0);
    } else {
      this.getSharesCount(
        this.$stakeholder.value,
        peque.SHARE_RECEIVER_TYPE_STAKEHOLDER,
        this.$date.value,
        this.$shareClass.value
      ).then(setCount);
    }
  }

  setupAllStakeholders() {
    return new Promise((resolve, reject) => {
      this.setupStakeholders().then((stakeholders) => {
        const $stakeholderSelect = this.$form.querySelector(
          '[name="stakeholder-receiver"]'
        );
        const $beneficiarySelect = this.$form.querySelector(
          '[name="beneficiary-receiver"]'
        );

        this.setupStakeholderSelectors(
          stakeholders,
          $stakeholderSelect,
          $beneficiarySelect,
          resolve
        );
      });
    });
  }

  getSharesCount(receiverId, receiverType, date, shareClass) {
    return new Promise((resolve, reject) => {
      receiverId = parseInt(receiverId);
      receiverType = parseInt(receiverType);
      shareClass = parseInt(shareClass);

      // it's missing data, do nothing, not even rejecting the promise
      if (date.length < 1 || isNaN(shareClass)) {
        return;
      }

      peque
        .api(
          "shares/count",
          {
            receiver_id: receiverId,
            receiver_type: receiverType,
            date: date,
            class: shareClass,
            simulation: $_GET("simulation"),
          },
          "get"
        )
        .then((response) => {
          let amount = response.result;

          // sum current value as available if it's the same stakeholder
          if (this.currentData && receiverId === this.currentData.stakeholder) {
            amount += this.currentData.amount;
          }

          resolve(amount);
        });
    });
  }

  validateAndSendForm() {
    const $numerationError = this.$form.querySelector(
      "[data-numeration-error]"
    );
    if ($numerationError) {
      $numerationError.classList.add("d-none");
    }
    this.getFields(this.formFields)
      .then((data) => {
        data.company = this.companyId;
        data.simulation =
          typeof window.pequeData.simulation === "undefined"
            ? $_GET("simulation")
            : window.pequeData.simulation;
        data.type = peque.TRANSACTION_TYPE_TRANSFER;

        if (data.stakeholder === data.stakeholder_receiver) {
          return this.showErrorOnField(
            trans("sender_and_receiver_can_t_be_the_same"),
            this.$stakeholder
          );
        }

        peque
          .api("transactions", JSON.stringify(data), "post")
          .then((response) => {
            this.mutateReactTransactions();
          })
          .catch((error) => {
            this.enableSubmitButton(true);

            if (typeof error.field === "undefined") {
              throw error;
            }
            this.showErrorOnField(
              error.result,
              this.$form.querySelector('[name="' + error.field + '"]')
            );

            if (error.field === "numeration" && $numerationError) {
              $numerationError.classList.remove("d-none");
              $numerationError.innerText = error.result;
            }
          });
      })
      .catch((error) => {
        if (typeof error.el === "undefined") {
          throw error;
        }
        this.showErrorOnField(error.message, error.el);
      });
  }
}
